import { useAuth } from '@agentnet/auth';
import { DataTable } from '@fluentsms/agentnet-web-components';
import { createStyles, Grid, makeStyles } from '@material-ui/core';
import { CellClickedEvent, ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import { getCountiesPropertyFromList } from 'api/file/file-property-api';
import { CountyType } from 'api/file/interfaces/get-file';
import { getEndorsements } from 'api/jacket-api';
import {
  assignCplApi,
  createFileApi,
  getCplTypesApi,
  getPolicyInventoryApi,
} from 'api/orderManagement/order-management-api';
import { getAccountsByFirmId } from 'api/profile/profile-api';
import { FirmAccount } from 'api/profile/types';
import { Endorsement } from 'features/jackets/types';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import useAsync from 'hooks/useAsync';
import { useContext, useEffect, useState } from 'react';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import { AgentNetDivider } from 'ui-kit/components/dividers';
import FormDrawerComponent from 'ui-kit/components/drawer/FormDrawerComponent';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import { DeleteOutlineIcon } from 'ui-kit/icons/DeleteOutlineIcon';
import { AgentNetDropdownSelector, AgentNetTextInput } from 'ui-kit/inputs';
import { SelectOption } from 'ui-kit/inputs/AgentNetDropdownSelector';
import { doValidate, FieldValidationError } from 'utilities/validation/validation';
import * as yup from 'yup';
import USPostalCodeField from '../../ui-kit/inputs/USPostalCodeField/USPostalCodeField';
import { statCodeStateList } from './createNewFileConstants';
import { CreateNewFileJacket } from './CreateNewFileJacket';
import { UnderlinedCurrencyEditor, UnderlinedCurrencyEditorStyle } from './CurrencyEditor';
import './OrderManagement.scss';
import {
  AssignCplRequestModel,
  AssignCplResponseModel,
  CplTypeResponseModel,
  CreateFileRequestModel,
  CreateFileResponseModel,
  PolicyInventoryType,
} from './types';
import { makeRandomId } from 'utilities/utilities';

export interface CreateNewFileJacketEndorsementData extends Endorsement {
  gross?: number;
  net?: number;
  statCode?: string;
  [id: string]: any | undefined;
}

export interface CreateNewFileJacketCplFee extends CplTypeResponseModel {
  fee: number;
  feeInput?: string;
  [id: string]: any | undefined;
}

export interface CreateNewFileJacketData {
  policyNumber: string;
  liabilityAmount: number;
  policyDate: string;
  reportedGross: number;
  reportedNet: number;
  rateTypeId: number;
  priorPolicyAmount?: number;
  priorPolicyDate?: string;
  statCode?: string;
  endorsements: CreateNewFileJacketEndorsementData[];
}

interface CreateNewFileData {
  accountNumber: string;
  fileNumber: string;
  salesPrice: number;
  loanAmount: number;
  closingDate: string;
  naicPropertyType: string;
  address1: string;
  address2: string;
  city: string;
  stateCode: string;
  zipCode: string;
  countyCode: string;
}

interface CreateNewFileDrawerProps {
  openDrawer: boolean;
  setOpenDrawer: (isOpen: boolean) => void;
  onNewFileCreated: (file: CreateFileResponseModel) => Promise<void>;
}

function constructCreateNewFileJacketData(): CreateNewFileJacketData {
  return {
    policyNumber: '',
    liabilityAmount: 0,
    policyDate: '',
    reportedGross: 0,
    reportedNet: 0,
    rateTypeId: 0,
    endorsements: [],
  };
}

function constructCreateNewFileData(): CreateNewFileData {
  return {
    accountNumber: '',
    fileNumber: '',
    salesPrice: 0,
    loanAmount: 0,
    closingDate: '',
    naicPropertyType: '',
    address1: '',
    address2: '',
    city: '',
    stateCode: '',
    zipCode: '',
    countyCode: '',
  };
}

function getRequestDate(policyDate: string | undefined): string | undefined {
  if (!policyDate) {
    return undefined;
  }
  const ticks = Date.parse(policyDate);
  if (isNaN(ticks)) {
    return undefined;
  }
  const date = new Date(ticks);
  return (
    date.getFullYear() +
    '-' +
    (date.getMonth() + 1).toString().padStart(2, '0') +
    '-' +
    date.getDate().toString().padStart(2, '0')
  ); // YYYY-MM-DD
}

function getCountiesSelectOptions(counties: CountyType | null): SelectOption[] {
  if (!counties) {
    return [];
  }
  const countySelectOptions: SelectOption[] = counties.counties.map((county) => ({
    value: county.toUpperCase(),
    name: county.toUpperCase(),
  }));
  return countySelectOptions.sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? -1);
}

function getCplTypesSelectOptions(cplTypes: CplTypeResponseModel[]): SelectOption[] {
  if (!cplTypes || cplTypes.length === 0) {
    return [];
  }
  const cplTypeOptions = cplTypes.map((cplType) => ({
    value: cplType,
    name: cplType.starsItemNumber,
  }));
  return cplTypeOptions;
}

function getAccountNumberSelectOptions(firmAccounts: FirmAccount[]): SelectOption[] {
  if (!firmAccounts || firmAccounts.length === 0) {
    return [];
  }
  const accountNumberSet = new Set<string>();
  const accountNumberSelectOptions: SelectOption[] = [];
  for (const account of firmAccounts) {
    const accountNumber = account.accountNumber?.trim();
    // Only include accounts with valid accountId (required for counties/endorsements API calls)
    // Note: accountId can be 0, so we check for undefined/null specifically
    if (accountNumber && account.accountId != null) {
      if (accountNumberSet.has(accountNumber)) {
        continue;
      }
      accountNumberSelectOptions.push({
        value: account,
        name: accountNumber + (account.status === 'Inactive' ? ' (Inactive)' : ''),
      });
      accountNumberSet.add(accountNumber);
    }
  }
  return accountNumberSelectOptions.sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? -1);
}

function getNaicPropertyTypeSelectOptions(): SelectOption[] {
  return [
    { value: 'Residential (1-4 family)', name: 'Residential (1-4 family)' },
    { value: 'Non-residential', name: 'Non-residential' },
    { value: 'Commercial', name: 'Commercial' },
  ];
}

const naicPropertyTypeSelectOptions = getNaicPropertyTypeSelectOptions();

const useStyles = makeStyles(() =>
  createStyles({
    addJacketButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'right',
    },
  }),
);

function getSelectedPoliciesInOtherJackets(jackets: CreateNewFileJacketData[], index: number): string[] {
  return jackets.filter((_, i) => i !== index).map((jacket) => jacket.policyNumber);
}

const UnderlinedInputRenderer = (params: ICellRendererParams<CreateNewFileJacketCplFee>) => {
  const numericValue = Number(params.value);
  if (isNaN(numericValue) || !params.data) {
    return <div style={UnderlinedCurrencyEditorStyle}>$0.00</div>;
  }
  params.data.fee = Number(numericValue.toFixed(2));
  const twoPlaces = numericValue.toFixed(2).toString();
  const formatted = `$${twoPlaces.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  return <div style={UnderlinedCurrencyEditorStyle}>{formatted}</div>;
};

export function CreateNewFileDrawer({
  openDrawer,
  setOpenDrawer,
  onNewFileCreated,
}: CreateNewFileDrawerProps): JSX.Element {
  const { getAccessToken } = useAuth();
  const { addGlobalMsg } = useGlobalMessages();
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);
  const [jacketValidationErrors, setJacketValidationErrors] = useState<FieldValidationError[][]>([[]]);
  const [cplFeeValidationErrors, setCplFeeValidationErrors] = useState<CreateNewFileJacketCplFee[]>([]);
  const [data, setData] = useState<CreateNewFileData>(constructCreateNewFileData());
  const [jackets, setJackets] = useState<CreateNewFileJacketData[]>([]);
  const [policies, setPolicies] = useState<PolicyInventoryType[]>([]);
  const [account, setAccount] = useState<FirmAccount | null>(null);
  const [endorsements, setEndorsements] = useState<CreateNewFileJacketEndorsementData[]>([]);
  const [fileQueueing, setFileQueueing] = useState(false);
  const [counties, setCounties] = useState<CountyType | null>(null);
  const [cplTypes, setCplTypes] = useState<CplTypeResponseModel[]>([]);
  const [cplFees, setCplFees] = useState<CreateNewFileJacketCplFee[]>([]);
  const [firmAccounts, setFirmAccounts] = useState<FirmAccount[]>([]);

  const classes = useStyles();

  const statCodeRequired = statCodeStateList.includes(data.stateCode);

  const createNewFileJacketTypeSchema = yup.object().shape({
    liabilityAmount: yup.number().required(),
    policyDate: yup.string().required(),
    reportedGross: yup.number().required(),
    reportedNet: yup.number().required(),
    rateTypeId: yup.number().required().moreThan(0),
    statCode: statCodeRequired ? yup.string().required() : yup.string().notRequired(),
  });

  const createNewFileTypeSchema = yup.object().shape({
    accountNumber: yup.string().required(),
    fileNumber: yup.string().required(),
    naicPropertyType: yup.string().required(),
    stateCode: yup.string().required(),
    countyCode: yup.string().required(),
  });

  const cplFeesColumnConfig: ColDef<CreateNewFileJacketCplFee>[] = [
    {
      headerName: 'Type',
      field: 'starsItemNumber',
      maxWidth: 400,
      minWidth: 400,
      cellRenderer: (params: ICellRendererParams<CreateNewFileJacketCplFee>) => {
        const value = cplTypes.find((cpl) => cpl.cplId === params.data?.cplId) ?? '';
        return (
          <AgentNetDropdownSelector
            label=""
            name="CplTypes"
            options={getCplTypesSelectOptions(cplTypes)}
            value={value}
            menuOption={(cplType: CplTypeResponseModel) => {
              const foundIndex = cplFees.findIndex((cpl) => cpl.id === params.data?.id);
              if (foundIndex > -1) {
                const cplFee = cplFees[foundIndex];
                cplFee.cplGroupId = cplType.cplGroupId;
                cplFee.cplId = cplType.cplId;
                cplFee.cplDesc = cplType.cplDesc;
                cplFee.starsItemNumber = cplType.starsItemNumber;
                cplFee.stateCode = cplType.stateCode;
                cplFee.cplGroupDesc = cplType.cplGroupDesc;
                cplFee.coveredPartyTypeCdId = cplType.coveredPartyTypeCdId;
                cplFee.underwriterId = cplType.underwriterId;
                //cplFee.fee = params.data?.fee || 0;
                setCplFees([...cplFees]);
              }
            }}
            showValidation
            error={cplFeeValidationErrors?.some((cplFee) => cplFee.id === params.data?.id)}
          />
        );
      },
      editable: false,
      suppressHeaderMenuButton: true,
    },
    {
      colId: 'feeInput',
      headerName: 'Fee',
      field: 'feeInput',
      minWidth: 342,
      maxWidth: 342,
      cellRenderer: UnderlinedInputRenderer,
      cellEditor: UnderlinedCurrencyEditor,
      onCellClicked: (params: CellClickedEvent) => {
        params.api.startEditingCell({
          rowIndex: params.rowIndex ?? 0,
          colKey: 'feeInput',
        });
      },
      type: 'rightAligned',
      editable: true, // handled by renderer
      suppressHeaderMenuButton: true,
    },
    {
      headerName: '',
      floatingFilterComponentParams: { suppressFilterButton: true },
      suppressMenu: true,
      floatingFilter: false,
      sortable: false,
      width: 60,
      cellStyle: { display: 'flex', justifyContent: 'center', cursor: 'pointer', alignItems: 'center' },
      pinned: 'right',
      cellRenderer: DeleteOutlineIcon,
      onCellClicked: (params) => {
        const { data } = params;
        const foundIndex = cplFees.findIndex((cpl) => cpl.id === data?.id);
        if (foundIndex > -1) {
          cplFees.splice(foundIndex, 1);
          setCplFees([...cplFees]);
        }
      },
      suppressHeaderMenuButton: true,
    },
  ];

  const handleCancelCreate = () => {
    setOpenDrawer(false);
  };

  const getCplTypes = async (account: FirmAccount): Promise<CplTypeResponseModel[]> => {
    const token = await getAccessToken();
    return await getCplTypesApi(account.accountNumber, token);
  };

  const {
    execute: executeGetCplTypes,
    status: getCplTypesStatus,
    errors: getCplTypesErrors,
    value: getCplTypesValue,
  } = useAsync<CplTypeResponseModel[]>(getCplTypes, false);

  const getCountiesApi = async (account: FirmAccount): Promise<CountyType> => {
    if (account.accountId == null) {
      throw new Error('Account ID is required to fetch counties');
    }
    const token = await getAccessToken();
    return getCountiesPropertyFromList(account.stateCode, account.accountId, token);
  };

  const {
    execute: executeGetCounties,
    status: getCountiesStatus,
    errors: getCountiesErrors,
    value: getCountiesValue,
  } = useAsync<CountyType>(getCountiesApi, false);

  const getEndorsementsApi = async (account: FirmAccount): Promise<Endorsement[]> => {
    const token = await getAccessToken();
    return await getEndorsements(account?.stateCode || '', account?.underwriterCode || '', token);
  };

  const {
    execute: executeGetEndorsements,
    status: getEndorsementsStatus,
    errors: getEndorsementsErrors,
    value: getEndorsementsValue,
  } = useAsync<Endorsement[]>(getEndorsementsApi, false);

  const createFile = async (file: CreateFileRequestModel): Promise<CreateFileResponseModel> => {
    const token = await getAccessToken();
    return await createFileApi(file, token);
  };

  const {
    execute: executeCreateFile,
    status: createFileStatus,
    errors: createFileErrors,
    value: createFileValue,
  } = useAsync<CreateFileResponseModel>(createFile, false);

  const getPolicyInventoryDetails = async (account: FirmAccount): Promise<PolicyInventoryType[]> => {
    const token = await getAccessToken();
    return await getPolicyInventoryApi(account.accountNumber, token);
  };

  const {
    execute: executeGetPolicyInventoryDetails,
    status: getPolicyInventoryDetailsStatus,
    errors: getPolicyInventoryDetailsErrors,
    value: getPolicyInventoryDetailsValue,
  } = useAsync<PolicyInventoryType[]>(getPolicyInventoryDetails, false);

  const fetchFirmAccounts = async (): Promise<FirmAccount[]> => {
    const token = await getAccessToken();
    const firmId = profileCtx.userFirm?.firmId;
    if (!firmId) {
      return [];
    }
    return await getAccountsByFirmId(firmId, token);
  };

  const {
    execute: executeGetFirmAccounts,
    status: getFirmAccountsStatus,
    errors: getFirmAccountsErrors,
    value: getFirmAccountsValue,
  } = useAsync<FirmAccount[]>(fetchFirmAccounts, false);

  useEffect(() => {
    if (openDrawer && profileCtx.userFirm?.firmId) {
      executeGetFirmAccounts();
    }
  }, [openDrawer, profileCtx.userFirm?.firmId]);

  useEffect(() => {
    if (getFirmAccountsStatus === 'success' && getFirmAccountsValue) {
      setFirmAccounts(getFirmAccountsValue);
    } else if (getFirmAccountsStatus === 'error') {
      console.error(getFirmAccountsErrors);
      addGlobalMsg({
        message: 'There was an error while retrieving accounts.',
        type: 'error',
      });
    }
  }, [getFirmAccountsStatus]);

  useEffect(() => {
    if (getPolicyInventoryDetailsStatus === 'success' && getPolicyInventoryDetailsValue) {
      setPolicies(
        getPolicyInventoryDetailsValue
          .filter((x) => x.stateList.includes(data.stateCode))
          .sort((a, b) => a.itemNo.localeCompare(b.itemNo)),
      );
    } else if (getPolicyInventoryDetailsStatus === 'error') {
      console.error(getPolicyInventoryDetailsErrors);
      addGlobalMsg({
        message: 'There was an error while retrieving Policies.',
        type: 'error',
      });
    }
  }, [getPolicyInventoryDetailsStatus]);

  useEffect(() => {
    if (getEndorsementsStatus === 'success' && getEndorsementsValue) {
      setEndorsements(getEndorsementsValue.sort((a, b) => a.endorsementName.localeCompare(b.endorsementName)));
    } else if (getEndorsementsStatus === 'error') {
      console.error(getEndorsementsErrors);
      addGlobalMsg({
        message: 'There was an error while retrieving Endorsements.',
        type: 'error',
      });
    }
  }, [getEndorsementsStatus]);

  useEffect(() => {
    if (getCountiesStatus === 'success' && getCountiesValue) {
      setCounties(getCountiesValue);
    } else if (getCountiesStatus === 'error') {
      setCounties(null);
      console.error(getCountiesErrors);
      addGlobalMsg({
        message: 'There was an error while retrieving Counties.',
        type: 'error',
      });
    }
  }, [getCountiesStatus]);

  useEffect(() => {
    if (getCplTypesStatus === 'success' && getCplTypesValue) {
      setCplTypes(getCplTypesValue);
    } else if (getCplTypesStatus === 'error') {
      setCplTypes([]);
      console.error(getCplTypesErrors);
      addGlobalMsg({
        message: 'There was an error while retrieving CPL Types.',
        type: 'error',
      });
    }
  }, [getCplTypesStatus]);

  const assignCplsToFileApi = async (fileId: number): Promise<void> => {
    for (const cplFee of cplFees) {
      await assignCplsToFile(fileId, cplFee).catch((error) => {
        console.error('Error assigning CPL to file:', error);
        addGlobalMsg({
          message: 'There was an error while assigning CPL to file.',
          type: 'error',
        });
      });
    }
  };

  const assignCplsToFile = async (
    fileId: number,
    cplFee: CreateNewFileJacketCplFee,
  ): Promise<AssignCplResponseModel> => {
    const token = await getAccessToken();
    const request: AssignCplRequestModel = {
      fileId: fileId,
      stateCode: data.stateCode,
      cplGroupId: cplFee.cplGroupId,
      cplId: cplFee.cplId,
      itemNumber: cplFee.starsItemNumber,
      accountNumber: Number(data.accountNumber),
      rateItems: [
        {
          isSimul: false,
          faccRateTypeCode: '',
          starsTaxCode: '',
          rateFeeDescr: cplFee.starsItemNumber,
          endorsementStarsItemNumber: '',
          selectedStarsStatCode: '',
          calculatedAmount: cplFee.fee,
          netDueAmount: cplFee.fee,
          rateFeeTypeCdId: 505, // CPLFE
        },
      ],
    };

    return await assignCplApi(request, token);
  };

  useEffect(() => {
    if (createFileStatus === 'success' && createFileValue) {
      assignCplsToFileApi(createFileValue.fileId).then(() => {
        onNewFileCreated(createFileValue).then(() => {
          addGlobalMsg({
            message: 'Your file was created successfully.',
            type: 'success',
          });
          setData(constructCreateNewFileData);
          setJackets([]);
          setPolicies([]);
          setAccount(null);
          setEndorsements([]);
          setFileQueueing(false);
          setCplFees([]);
          setFirmAccounts([]);
          setOpenDrawer(false);
        });
      });
    } else if (createFileStatus === 'error') {
      console.error(createFileErrors);
      addGlobalMsg({
        message: 'There was an error while creating the file.',
        type: 'error',
      });
      setFileQueueing(false);
    }
  }, [createFileStatus]);

  return (
    <FormDrawerComponent
      title={'Create New File'}
      open={openDrawer}
      primaryActionLabel="Create File & Add to Queue"
      primaryActionProps={{ loading: fileQueueing, disabled: fileQueueing }}
      onPrimaryAction={async () => {
        const errors: FieldValidationError[] = await doValidate(data, createNewFileTypeSchema);
        if (errors) {
          setValidationErrors(errors);
        }

        const jacketErrors: FieldValidationError[][] = [];
        for (const jacket of jackets) {
          const jacketErrorsForJacket: FieldValidationError[] = await doValidate(jacket, createNewFileJacketTypeSchema);
          jacketErrors.push(jacketErrorsForJacket);
        }
        setJacketValidationErrors(jacketErrors);

        const invalidCplFees = cplFees.filter((cplFee) => !cplFee.cplId || !cplFee.fee);
        setCplFeeValidationErrors(invalidCplFees);

        const missingStatCodes = jackets.some(
          (jacket) =>
            statCodeRequired && (!jacket.statCode || jacket.endorsements.some((endorsement) => !endorsement.statCode)),
        );

        if (
          errors?.length ||
          jacketErrors?.some((jacketError) => jacketError?.length) ||
          invalidCplFees.length > 0 ||
          missingStatCodes
        ) {
          addGlobalMsg({
            message: 'There are required fields that must be filled out.',
            type: 'error',
          });
          return;
        }

        const file: CreateFileRequestModel = {
          fileNumber: data.fileNumber,
          accountNumber: Number(data.accountNumber),
          propertyType: data.naicPropertyType,
          taxCode: '',
          address: data.address1,
          cityName: data.city,
          stateCode: data.stateCode,
          zipCode: data.zipCode,
          countyName: data.countyCode,
          policyJackets: jackets.map((jacket) => {
            const jacketPieces = jacket.policyNumber.split('-');
            const itemNumber = jacketPieces[0];
            return {
              jacketPrefix: itemNumber,
              jacketSuffix: jacketPieces[1],
              policyNumber: jacket.policyNumber,
              itemNumber: itemNumber,
              liabilityAmount: jacket.liabilityAmount,
              policyDate: getRequestDate(jacket.policyDate),
              priorPolicyDate: getRequestDate(jacket.priorPolicyDate),
              priorPolicyAmount: jacket.priorPolicyAmount,
              rateItems: [
                {
                  isSimul: false,
                  faccRateTypeCode: jacket.rateTypeId.toString(),
                  starsTaxCode: '',
                  rateFeeDescr: jacket.policyNumber,
                  endorsementSTARSItemNumber: itemNumber,
                  selectedSTARSStatCode: jacket.statCode || '',
                  calculatedAmount: jacket.reportedGross,
                  netDueAmount: jacket.reportedNet,
                  rateFeeTypeCdId: 504, // JACKETCHRG
                },
                ...jacket.endorsements.map((endorsement) => {
                  return {
                    isSimul: false,
                    faccRateTypeCode: jacket.rateTypeId.toString(),
                    starsTaxCode: '',
                    rateFeeDescr: endorsement.endorsementName,
                    endorsementSTARSItemNumber: endorsement.endorsementFormNumber,
                    selectedSTARSStatCode: endorsement.statCode || '',
                    calculatedAmount: endorsement.gross || 0,
                    netDueAmount: endorsement.net || 0,
                    rateFeeTypeCdId: 503, // ENDORFEE
                  };
                }),
              ],
            };
          }),
        };
        setFileQueueing(true);
        executeCreateFile(file);
      }}
      onDismissAction={handleCancelCreate}
      testId="scrollable-form-drawer"
      width={850}
    >
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <AgentNetDropdownSelector
            label={'Account Number'}
            name={'AccountNumber'}
            qaAttribute={'Create-New-File-AccountNumber'}
            options={getAccountNumberSelectOptions(firmAccounts)}
            value={account}
            menuOption={(account: FirmAccount) => {
              if (account.accountNumber !== data.accountNumber) {
                data.accountNumber = account.accountNumber;
                data.stateCode = account.stateCode;

                setJackets([]);
                setCplFees([]);
                setData({ ...data });

                executeGetPolicyInventoryDetails(account);
                executeGetEndorsements(account);
                executeGetCounties(account);
                executeGetCplTypes(account);
                setAccount(account);
              }
            }}
            required
            showValidation
            error={validationErrors?.some((error) => error.field === 'accountNumber')}
            errs={validationErrors}
          />
        </Grid>
        <Grid item sm={6}>
          <AgentNetTextInput
            fullWidth
            label={'File Number'}
            value={data.fileNumber}
            onChange={(event) => {
              if (event.target.value !== data.fileNumber) {
                data.fileNumber = event.target.value;
                setData({ ...data });
              }
            }}
            required
            showValidation
            error={validationErrors?.some((error) => error.field === 'fileNumber')}
            errs={validationErrors}
            helperText={validationErrors?.some((error) => error.field === 'fileNumber') && 'File Number is required'}
          />
        </Grid>
        <Grid item sm={12}>
          <AgentNetDivider typoVariant="h3" title={'Property'} disablePadding />
        </Grid>
        <Grid item xs={6}>
          <AgentNetDropdownSelector
            label={'NAIC Property Type'}
            name={'NAIC Property Type'}
            value={data.naicPropertyType}
            menuOption={(naicPropertyType) => {
              if (naicPropertyType !== data.naicPropertyType) {
                data.naicPropertyType = naicPropertyType;
                setData({ ...data });
              }
            }}
            options={naicPropertyTypeSelectOptions}
            required
            showValidation
            error={validationErrors?.some((error) => error.field === 'naicPropertyType')}
            errs={validationErrors}
          />
        </Grid>
        <Grid item sm={6} />
        <Grid item sm={6}>
          <AgentNetTextInput
            value={data.address1}
            onChange={(event) => {
              if (event.target.value !== data.address1) {
                data.address1 = event.target.value;
                setData({ ...data });
              }
            }}
            fullWidth
            label={'Address 1'}
          />
        </Grid>
        <Grid item sm={3}>
          <AgentNetTextInput
            value={data.city}
            onChange={(event) => {
              if (event.target.value !== data.city) {
                data.city = event.target.value;
                setData({ ...data });
              }
            }}
            fullWidth
            label={'City'}
          />
        </Grid>
        <Grid item sm={3}>
          <AgentNetTextInput
            fullWidth
            label={'State'}
            value={data.stateCode}
            disabled
            required
            showValidation
            error={validationErrors?.some((error) => error.field === 'stateCode')}
            errs={validationErrors}
          />
        </Grid>
        <Grid item sm={3}>
          <USPostalCodeField
            variant="outlined"
            fullWidth
            value={data.zipCode}
            onChange={(event) => {
              if (event.target.value !== data.zipCode) {
                data.zipCode = event.target.value;
                setData({ ...data });
              }
            }}
            label="Zip"
          />
        </Grid>
        <Grid item sm={3}>
          <AgentNetDropdownSelector
            label={'County'}
            name={'County'}
            qaAttribute={'Create-New-File-County'}
            options={getCountiesSelectOptions(counties)}
            value={data.countyCode}
            menuOption={(countyCode: string) => {
              data.countyCode = countyCode;
              setData({ ...data });
            }}
            required
            showValidation
            error={validationErrors?.some((error) => error.field === 'countyCode')}
            errs={validationErrors}
          />
        </Grid>
        <Grid item sm={3} />
        <Grid item sm={6}>
          <AgentNetDivider typoVariant="h3" title={'Jackets'} disablePadding />
        </Grid>
        <Grid item sm={3} />
        <Grid item sm={3}>
          <div className={classes.addJacketButton}>
            <AgentNetButton
              color="primary"
              size="small"
              variant="contained"
              onClick={() => {
                jackets.push(constructCreateNewFileJacketData());
                setJackets([...jackets]);
              }}
              disabled={
                getEndorsementsStatus !== 'success' ||
                getPolicyInventoryDetailsStatus !== 'success' ||
                policies.length === 0 ||
                !data.countyCode ||
                !data.stateCode ||
                !data.accountNumber ||
                !data.fileNumber ||
                !data.naicPropertyType
              }
              plusIcon
              fullWidth={true}
            >
              Add Jacket
            </AgentNetButton>
          </div>
        </Grid>
        {jackets.map((jacket, index) => (
          <CreateNewFileJacket
            key={index}
            account={account}
            stateCode={data.stateCode}
            countyCode={data.countyCode}
            policies={policies}
            selectedPolicies={getSelectedPoliciesInOtherJackets(jackets, index)}
            endorsements={endorsements}
            jacket={jacket}
            onChange={(key, jacket) => {
              if (!jacket) {
                jackets.splice(key, 1);
              } else {
                jackets[key] = jacket;
              }
              setJackets([...jackets]);
            }}
            validationErrors={jacketValidationErrors.length > index ? jacketValidationErrors[index] : undefined}
          />
        ))}
        <Grid item sm={9}>
          <AgentNetDivider typoVariant="h3" title={'Closing Protection Letters'} disablePadding />
        </Grid>
        <Grid item sm={3}>
          <AgentNetButton
            color="primary"
            size="small"
            variant="contained"
            onClick={() => {
              const cplFee: CreateNewFileJacketCplFee = {
                id: makeRandomId(),
                cplGroupId: 0,
                cplGroupDesc: '',
                coveredPartyTypeCdId: 0,
                coveredPartyTypeDesc: '',
                stateCode: data.stateCode,
                underwriterId: 0,
                cplId: 0,
                cplDesc: '',
                starsItemNumber: '',
                fee: 0,
                feeInput: '',
              };
              setCplFees([...cplFees, cplFee]);
            }}
            disabled={cplTypes.length === 0}
            fullWidth={true}
            plusIcon
          >
            Add CPL
          </AgentNetButton>
        </Grid>
        {cplFees.length > 0 ? (
          <div style={{ height: 200, width: '100%' }}>
            <DataTable
              rowData={cplFees}
              columnDefs={cplFeesColumnConfig}
              animateRows={false}
              noRowsOverlayComponent="customOverlayNoRows"
              stopEditingWhenCellsLoseFocus={true}
              rowHeight={40}
              suppressRowHoverHighlight={true}
              columnHoverHighlight={false}
              rowClass=""
              rowStyle={{ background: 'none' }}
            />
          </div>
        ) : null}
      </Grid>
    </FormDrawerComponent>
  );
}
